import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Save,
  Edit,
  Check
} from "lucide-react";
import { toast } from "sonner";
import NestedHauoraRings from "../components/NestedHauoraRings";

const MyPou = () => {
  const { user } = useAuth();
  const [dailyGoals, setDailyGoals] = useState(null);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form state for daily goals
  const [formData, setFormData] = useState({
    tinanaGoalTitle: "",
    tinanaGoalDesc: "",
    hinengaroGoalTitle: "",
    hinengaroGoalDesc: "",
    wairuaGoalTitle: "",
    wairuaGoalDesc: "",
    whanauGoalTitle: "",
    whanauGoalDesc: ""
  });

  const today = new Date().toISOString().split('T')[0];

  const pillars = [
    { 
      key: "wairua", 
      name: "Taha Wairua", 
      subtitle: "Spiritual Wellbeing",
      color: "#5B8BA0",
      placeholder: "e.g., Meditation, prayer, nature walk"
    },
    { 
      key: "hinengaro", 
      name: "Taha Hinengaro", 
      subtitle: "Mental Wellbeing",
      color: "#D4A84B",
      placeholder: "e.g., Read, journal, learn something new"
    },
    { 
      key: "tinana", 
      name: "Taha Tinana", 
      subtitle: "Physical Wellbeing",
      color: "#B85C4C",
      placeholder: "e.g., Exercise, stretch, walk"
    },
    { 
      key: "whanau", 
      name: "Taha Whānau", 
      subtitle: "Family Wellbeing",
      color: "#3D6B5A",
      placeholder: "e.g., Call whānau, quality time"
    }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [goalsRes, checkinRes] = await Promise.all([
        axios.get(`${API}/pou/daily-goals`),
        axios.get(`${API}/pou/checkin/${today}`)
      ]);
      
      if (goalsRes.data) {
        setDailyGoals(goalsRes.data);
        setFormData({
          tinanaGoalTitle: goalsRes.data.tinanaGoalTitle || "",
          tinanaGoalDesc: goalsRes.data.tinanaGoalDesc || "",
          hinengaroGoalTitle: goalsRes.data.hinengaroGoalTitle || "",
          hinengaroGoalDesc: goalsRes.data.hinengaroGoalDesc || "",
          wairuaGoalTitle: goalsRes.data.wairuaGoalTitle || "",
          wairuaGoalDesc: goalsRes.data.wairuaGoalDesc || "",
          whanauGoalTitle: goalsRes.data.whanauGoalTitle || "",
          whanauGoalDesc: goalsRes.data.whanauGoalDesc || ""
        });
      } else {
        setEditMode(true);
      }
      
      setTodayCheckin(checkinRes.data);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const saveGoals = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pou/daily-goals`, formData);
      toast.success("Daily goals saved!");
      setEditMode(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to save goals");
    } finally {
      setSaving(false);
    }
  };

  const toggleCompletion = async (pillar) => {
    const currentCheckin = todayCheckin || {
      tinanaCompleted: false,
      hinengaroCompleted: false,
      wairuaCompleted: false,
      whanauCompleted: false
    };
    
    const fieldMap = {
      tinana: "tinanaCompleted",
      hinengaro: "hinengaroCompleted",
      wairua: "wairuaCompleted",
      whanau: "whanauCompleted"
    };
    
    const newValue = !currentCheckin[fieldMap[pillar]];
    
    try {
      const res = await axios.post(`${API}/pou/checkin`, {
        date: today,
        tinanaCompleted: pillar === "tinana" ? newValue : currentCheckin.tinanaCompleted,
        hinengaroCompleted: pillar === "hinengaro" ? newValue : currentCheckin.hinengaroCompleted,
        wairuaCompleted: pillar === "wairua" ? newValue : currentCheckin.wairuaCompleted,
        whanauCompleted: pillar === "whanau" ? newValue : currentCheckin.whanauCompleted
      });
      setTodayCheckin(res.data);
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
      </div>
    );
  }

  const pouCompleted = todayCheckin ? [
    todayCheckin.tinanaCompleted,
    todayCheckin.hinengaroCompleted,
    todayCheckin.wairuaCompleted,
    todayCheckin.whanauCompleted
  ].filter(Boolean).length : 0;

  return (
    <div className="p-5 pb-8 max-w-lg mx-auto" data-testid="my-pou">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-brand text-[#0B3D2E] tracking-wide">My Taha</h1>
          <p className="text-sm text-[#4A5D56]">Daily Reoccurring Goals</p>
        </div>
        <Button 
          onClick={() => editMode ? saveGoals() : setEditMode(true)}
          disabled={saving}
          className={editMode 
            ? "bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full" 
            : "border border-[#0B3D2E] text-[#0B3D2E] bg-transparent hover:bg-[#0B3D2E]/5 rounded-full"
          }
          data-testid="edit-goals-btn"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          ) : editMode ? (
            <><Save className="w-4 h-4 mr-1" /> Save</>
          ) : (
            <><Edit className="w-4 h-4 mr-1" /> Edit</>
          )}
        </Button>
      </div>

      {/* Today's Progress */}
      <div className="flex items-center justify-center mb-8">
        <NestedHauoraRings
          wairuaCompleted={todayCheckin?.wairuaCompleted}
          hinengaroCompleted={todayCheckin?.hinengaroCompleted}
          tinanaCompleted={todayCheckin?.tinanaCompleted}
          whanauCompleted={todayCheckin?.whanauCompleted}
          size={140}
        />
        <div className="ml-6">
          <p className="text-3xl font-semibold text-[#0B3D2E]">{pouCompleted}/4</p>
          <p className="text-sm text-[#4A5D56]">completed today</p>
        </div>
      </div>

      {/* Pou Cards */}
      <div className="space-y-4">
        {pillars.map((pillar) => {
          const isCompleted = todayCheckin?.[`${pillar.key}Completed`];
          const goalTitle = formData[`${pillar.key}GoalTitle`];
          const goalDesc = formData[`${pillar.key}GoalDesc`];
          
          return (
            <Card 
              key={pillar.key} 
              className={`bg-white border rounded-2xl overflow-hidden transition-all ${
                isCompleted ? "border-[#0B3D2E]/20" : "border-[#0B3D2E]/5"
              }`}
              data-testid={`pou-card-${pillar.key}`}
            >
              <CardContent className="p-0">
                {/* Header with checkbox */}
                <div 
                  className={`flex items-center gap-4 p-4 cursor-pointer transition-colors ${
                    isCompleted ? "bg-[#E3EBE5]/50" : "hover:bg-[#F9F9F7]"
                  }`}
                  onClick={() => !editMode && toggleCompletion(pillar.key)}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${pillar.color}${isCompleted ? '' : '20'}` }}
                  >
                    {isCompleted ? (
                      <Check className="w-5 h-5 text-white" />
                    ) : (
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: pillar.color }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#051F17]">{pillar.name}</p>
                    <p className="text-xs text-[#4A5D56]">{pillar.subtitle}</p>
                  </div>
                  {!editMode && (
                    <div 
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isCompleted 
                          ? "border-[#0B3D2E] bg-[#0B3D2E]" 
                          : "border-[#0B3D2E]/30"
                      }`}
                    >
                      {isCompleted && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                  )}
                </div>

                {/* Goal Details / Edit Form */}
                {editMode ? (
                  <div className="p-4 pt-0 space-y-3 border-t border-[#0B3D2E]/5">
                    <div>
                      <Label className="text-xs text-[#4A5D56]">Daily Goal</Label>
                      <Input
                        placeholder={pillar.placeholder}
                        value={goalTitle}
                        onChange={(e) => setFormData({
                          ...formData,
                          [`${pillar.key}GoalTitle`]: e.target.value
                        })}
                        className="h-12 bg-[#F9F9F7] border-0 rounded-xl mt-1"
                        data-testid={`${pillar.key}-title-input`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#4A5D56]">Description (optional)</Label>
                      <Textarea
                        placeholder="Add more details..."
                        value={goalDesc}
                        onChange={(e) => setFormData({
                          ...formData,
                          [`${pillar.key}GoalDesc`]: e.target.value
                        })}
                        className="bg-[#F9F9F7] border-0 rounded-xl mt-1 min-h-[60px] resize-none"
                        data-testid={`${pillar.key}-desc-input`}
                      />
                    </div>
                  </div>
                ) : goalTitle && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="p-3 bg-[#F9F9F7] rounded-xl">
                      <p className="text-sm text-[#051F17]">{goalTitle}</p>
                      {goalDesc && (
                        <p className="text-xs text-[#4A5D56] mt-1">{goalDesc}</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Prompt to set goals if empty */}
      {!dailyGoals && !editMode && (
        <div className="text-center py-8">
          <p className="text-[#4A5D56] mb-4">
            Set your daily Pou goals to track your hauora journey
          </p>
          <Button 
            onClick={() => setEditMode(true)}
            className="bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full"
            data-testid="start-goals-btn"
          >
            Set Daily Goals
          </Button>
        </div>
      )}
    </div>
  );
};

export default MyPou;
